import React, { useEffect, useMemo, useRef, useState } from 'react';

function clamp(n, min, max) {
  const v = Number.isFinite(n) ? n : 0;
  return Math.max(min, Math.min(max, v));
}

function hashToInt(s) {
  try {
    const str = String(s || '');
    let h = 2166136261;
    for (let i = 0; i < str.length; i += 1) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  } catch {
    return 0;
  }
}

function fallbackPosFromId(id, bounds) {
  const h = hashToInt(id);
  const w = bounds.width;
  const hgt = bounds.height;
  const x = 160 + (h % Math.max(1, w - 320));
  const y = 160 + ((Math.floor(h / 997) % Math.max(1, hgt - 320)));
  return { x, y };
}

export default function BoardDesk(props) {
  const {
    posts,
    height = '70vh',
    frame = true,
    canDrag = false,
    onMove,
    renderFooter,
    renderStatusBadge = false,
    title,
    hint,
    className = ''
  } = props;

  const outerRef = useRef(null);
  const innerRef = useRef(null);

  const bounds = useMemo(() => ({ width: 4400, height: 2800 }), []);

  const initialPositions = useMemo(() => {
    const map = {};
    (posts || []).forEach((p) => {
      const pos = p?.pos;
      const x = Number(pos?.x);
      const y = Number(pos?.y);
      if (Number.isFinite(x) && Number.isFinite(y)) {
        map[p.id] = { x, y };
      } else {
        map[p.id] = fallbackPosFromId(p.id, bounds);
      }
    });
    return map;
  }, [posts, bounds]);

  const [posById, setPosById] = useState(initialPositions);
  useEffect(() => {
    setPosById(initialPositions);
  }, [initialPositions]);

  // Center view on mount
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    try {
      el.scrollLeft = (bounds.width - el.clientWidth) / 2;
      el.scrollTop = (bounds.height - el.clientHeight) / 2;
    } catch {
      // ignore
    }
  }, [bounds.width, bounds.height]);

  // Background pan via drag
  useEffect(() => {
    const host = outerRef.current;
    if (!host) return;

    let isPanning = false;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let startScrollTop = 0;

    const onPointerDown = (e) => {
      const target = e.target;
      // Do not pan when interacting with a note
      if (target && target.closest && target.closest('[data-note="true"]')) return;
      isPanning = true;
      startX = e.clientX;
      startY = e.clientY;
      startScrollLeft = host.scrollLeft;
      startScrollTop = host.scrollTop;
      try {
        host.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    };

    const onPointerMove = (e) => {
      if (!isPanning) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      host.scrollLeft = startScrollLeft - dx;
      host.scrollTop = startScrollTop - dy;
    };

    const onPointerUp = () => {
      isPanning = false;
    };

    host.addEventListener('pointerdown', onPointerDown);
    host.addEventListener('pointermove', onPointerMove);
    host.addEventListener('pointerup', onPointerUp);
    host.addEventListener('pointercancel', onPointerUp);
    return () => {
      host.removeEventListener('pointerdown', onPointerDown);
      host.removeEventListener('pointermove', onPointerMove);
      host.removeEventListener('pointerup', onPointerUp);
      host.removeEventListener('pointercancel', onPointerUp);
    };
  }, []);

  const startDragRef = useRef(null);

  const beginDrag = (e, postId) => {
    if (!canDrag) return;
    e.preventDefault();
    e.stopPropagation();
    const p = posById[postId] || fallbackPosFromId(postId, bounds);
    startDragRef.current = {
      postId,
      startX: e.clientX,
      startY: e.clientY,
      startPos: p
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  const moveDrag = (e) => {
    const st = startDragRef.current;
    if (!st) return;
    e.preventDefault();
    e.stopPropagation();

    const dx = e.clientX - st.startX;
    const dy = e.clientY - st.startY;

    const next = {
      x: clamp(st.startPos.x + dx, 40, bounds.width - 280),
      y: clamp(st.startPos.y + dy, 40, bounds.height - 220)
    };

    setPosById((prev) => ({ ...prev, [st.postId]: next }));
  };

  const endDrag = async (e) => {
    const st = startDragRef.current;
    if (!st) return;
    e.preventDefault();
    e.stopPropagation();
    startDragRef.current = null;

    const finalPos = posById[st.postId];
    if (!finalPos) return;
    if (onMove) {
      try {
        await onMove(st.postId, finalPos);
      } catch {
        // ignore (caller shows toast)
      }
    }
  };

  return (
    <div
      className={
        (frame
          ? 'rounded-2xl border border-border bg-card overflow-hidden '
          : 'bg-transparent overflow-hidden ') +
        (className || '')
      }
    >
      {frame && (title || hint) ? (
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
          <div>
            {title ? <div className="text-sm font-semibold">{title}</div> : null}
            {hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
          </div>
          <button
            type="button"
            className="text-xs px-3 py-2 rounded-xl border border-border bg-background hover:bg-muted"
            onClick={() => {
              const el = outerRef.current;
              if (!el) return;
              el.scrollLeft = (bounds.width - el.clientWidth) / 2;
              el.scrollTop = (bounds.height - el.clientHeight) / 2;
            }}
          >
            Centra
          </button>
        </div>
      ) : null}

      <div
        ref={outerRef}
        className="relative w-full overflow-auto scrollbar-hidden"
        style={{ height, touchAction: 'none' }}
      >
        <div
          ref={innerRef}
          className="relative"
          style={{
            width: bounds.width,
            height: bounds.height,
            backgroundImage:
              'radial-gradient(circle at 1px 1px, hsl(var(--muted-foreground) / 0.12) 1px, transparent 0), linear-gradient(135deg, hsl(var(--background)), hsl(var(--background)))',
            backgroundSize: '28px 28px, auto',
            backgroundPosition: '0 0, 0 0'
          }}
        >
          {(posts || []).map((post) => {
            const p = posById[post.id] || fallbackPosFromId(post.id, bounds);
            const status = post.status;
            const footer = typeof renderFooter === 'function' ? renderFooter(post) : null;

            return (
              <div
                key={post.id}
                data-note="true"
                className={
                  'absolute w-[260px] rounded-2xl border shadow-sm select-none ' +
                  (post.highlighted ? 'ring-2 ring-primary/40 ' : '')
                }
                style={{ left: p.x, top: p.y }}
              >
                <div
                  className={
                    'rounded-2xl overflow-hidden ' +
                    (status === 'pending'
                      ? 'bg-amber-50/90 text-slate-900 border border-amber-200'
                      : 'bg-card text-foreground border border-border')
                  }
                >
                  <div
                    className={
                      'px-3 py-2 border-b flex items-center justify-between gap-2 ' +
                      (status === 'pending' ? 'border-amber-200' : 'border-border')
                    }
                    onPointerDown={(e) => beginDrag(e, post.id)}
                    onPointerMove={moveDrag}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    style={{ cursor: canDrag ? 'grab' : 'default' }}
                    title={canDrag ? 'Trascina per spostare' : undefined}
                  >
                    <div className="text-xs font-semibold truncate">{post.author_name || 'Studente'}</div>
                    {renderStatusBadge && status ? (
                      <div
                        className={
                          'text-[10px] px-2 py-0.5 rounded-full border ' +
                          (status === 'pending'
                            ? 'border-amber-300 text-amber-800'
                            : 'border-border text-muted-foreground')
                        }
                      >
                        {status}
                      </div>
                    ) : null}
                  </div>

                  <div className="px-3 py-2">
                    <div className="text-sm whitespace-pre-wrap line-clamp-6">{post.text}</div>

                    {Array.isArray(post.uploads) && post.uploads.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {post.uploads.slice(0, 3).map((u) => (
                          <a
                            key={u.upload_id || u.url}
                            className="text-xs underline text-primary"
                            href={u.url}
                            target="_blank"
                            rel="noreferrer"
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            {u.is_image ? 'Immagine' : 'File'}
                          </a>
                        ))}
                      </div>
                    ) : null}

                    {post.column ? (
                      <div className="mt-2 text-[11px] text-muted-foreground">{post.column}</div>
                    ) : null}
                  </div>

                  {footer ? (
                    <div className="px-3 pb-3">
                      <div onPointerDown={(e) => e.stopPropagation()}>{footer}</div>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
